import React, { useState } from 'react';
import UploadFileButton from '../components/ui/utils/UploadFileButton';
import RestAPI from '../utils/restAPI';
import { logDebug } from '../shared/common/logger';

const ModelDetectionPage: React.FC = () => {
	const [imageData, setImageData] = useState<string | null>(null);
	const [detectionResult, setDetectionResult] = useState<any>(null);
	const [fetching, setFetching] = useState<boolean>(false);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	const onImageLoaded = (base64Data: string) => {
		setImageData(base64Data);
		setPreviewUrl(`data:image/jpeg;base64,${base64Data}`);
		setDetectionResult(null);
	}

	const processImage = async () => {
		if (!imageData) return;
		
		try {
			setFetching(true);
			const ret = await RestAPI.fetchAPI('objectDetection', 'POST', { base64Image: imageData });
			setDetectionResult(ret);
			logDebug('Object detection result:', ret);
			logDebug('Output image path:', ret?.outputs?.[0]?.output_image?.value);
		} catch (error) {
			logDebug('Error processing image:', error);
		}
		setFetching(false);
	}

	return (
		<div className="p-6">
			<h1 className="text-2xl font-bold mb-6">Model Detection</h1>
			
			<div className="mb-4">
				<UploadFileButton 
					onLoad={onImageLoaded} 
					accept="image/*" 
				/>
			</div>
			
			<div className="mb-8">
				<button 
					className="px-6 py-3 bg-blue-500 text-white rounded-lg disabled:bg-gray-300 hover:bg-blue-600 transition-colors"
					onClick={processImage} 
					disabled={!imageData || fetching}
				>
					{fetching ? 'Processing...' : 'Detect Objects'}
				</button>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

				{/* Detection Results Image */}
				{detectionResult && detectionResult.outputs && detectionResult.outputs[0] && detectionResult.outputs[0].output_image && detectionResult.outputs[0].output_image.value && (
					<div>
						<h2 className="text-lg font-semibold mb-2">Detection Results</h2>
						<img 
							src={`data:image/jpeg;base64,${detectionResult.outputs[0].output_image.value}`}
							alt="Detection results" 
							className="max-w-full h-auto border border-gray-300 rounded"
							onError={(e) => {
								logDebug('Image load error:', e);
								logDebug('Image src:', `data:image/jpeg;base64,${detectionResult.outputs[0].output_image.value.substring(0, 50)}...`);
							}}
							onLoad={() => logDebug('Image loaded successfully')}
						/>
						<div className="text-sm text-gray-600 mt-2">
							Base64 length: {detectionResult.outputs[0].output_image.value.length}
						</div>
					</div>
				)}

				{/* Raw JSON Results */}
				{detectionResult && (
					<div>
						<h2 className="text-lg font-semibold mb-2">Raw Data</h2>
						<div className="bg-gray-100 p-4 rounded max-h-96 overflow-auto">
							<pre className="text-xs">
								{JSON.stringify(detectionResult, null, 2)}
							</pre>
						</div>
					</div>
				)}
			</div>

			{/* Loading overlay */}
			{fetching && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white p-6 rounded-lg">
						<div className="text-center">Processing image...</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default ModelDetectionPage;